import React, { useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@buzzfeed/react-components';
import Subbuzzes from '../Subbuzzes';
import Headline from '../Headline';
import { ActionBar } from '../ActionBar';
import Package from '../Package/lazy';
import CustomHead from '../../pages/_head';
import InfiniteScrollTracking from './InfiniteScrollTracking';
import BuzzContext from '../../contexts/buzz';
import layoutStyles from '../Post/post.module.scss';
import { PACKAGE_VISIBILITY_OBSERVER_CONFIG } from '../../constants/intersection-observer';

const PostComponent = ({ buzz, subbuzzData, isActivePost = false }) => {
  const ref = useRef();
  const packageRef = useRef(null);
  const { setValue } = useContext(BuzzContext);
  const isMobile = useBreakpoint('(max-width:500px)');

  // Update URL and BuzzContext when post changes
  useEffect(() => {
    const nextUrl = `${buzz?.canonical_path || buzz?.canonical_url}?origin=shp-is`;
    const currentUrl = window.location.pathname + window.location.search;

    if (currentUrl !== nextUrl) {
      window.history.pushState(null, '', nextUrl);
    }

    setValue?.({ ...buzz, isInfinitePage: true });
  }, [buzz?.id, buzz?.canonical_path, buzz?.canonical_url, setValue]);

  // Notify parent when Package becomes visible (for sidebar stop behavior)
  useEffect(() => {
    if (!packageRef.current || typeof window === 'undefined') return;

    const element = packageRef.current; // Capture ref value for cleanup
    const observer = new IntersectionObserver(
      ([entry]) => {
        window.dispatchEvent(
          new CustomEvent('infiniteScrollPackageVisible', {
            detail: { isVisible: entry.isIntersecting, postId: buzz?.id },
          })
        );
      },
      PACKAGE_VISIBILITY_OBSERVER_CONFIG
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [buzz?.id]);

  return (
    <>
      <CustomHead
        buzz={buzz}
        subbuzzData={subbuzzData}
        badges={buzz.badges || []}
      />
      <InfiniteScrollTracking buzz={buzz} />
      <div
        ref={ref}
        data-infinite-post={buzz?.id}
        style={{ margin: '40px 0' }}
      >
        <Headline buzz={buzz} badges={[]} />
        <ActionBar
          buzz={buzz}
          commentCount={null}
          showShareBar={true}
          destination={buzz.destination_name}
          isFloatingButtonsEnd={false}
        />
        <Subbuzzes
          hideAds={!isActivePost}
          buzz={buzz}
          subbuzzData={subbuzzData}
          key={buzz.id}
          pixiedust={{
            unit_type: 'buzz_body',
            unit_name: buzz?.id,
            position_in_unit: 0,
          }}
        />
        
        {/* Full-width Package for infinite scroll posts */}
        <div ref={packageRef} className={layoutStyles.infiniteScrollPackageWrapper}>
          <Package
            isNewDesign={true}
            isShoppingDesign={true}
            limitedItems={isMobile ? 2 : 3}
          />
        </div>
      </div>
    </>
  );
};

PostComponent.propTypes = {
  buzz: PropTypes.object.isRequired,
  subbuzzData: PropTypes.shape({
    assets: PropTypes.object,
    subbuzzes: PropTypes.array,
  }),
  isActivePost: PropTypes.bool,
};

const Post = React.memo(PostComponent);
Post.displayName = 'Post';

export default Post;
